/**
 * Pantalla de apertura.
 *
 * Aparece mientras se cargan los catálogos y se desvanece sola. El logo entra
 * con una escala suave y un barrido de luz; nada rebota ni gira, porque esto
 * se ve doce veces al día y una animación llamativa cansa a la tercera.
 *
 * Respeta `prefers-reduced-motion`: con esa preferencia activa el logo
 * simplemente aparece.
 */

import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';

export function Bienvenida({
  visible,
  mensaje = 'Cargando…',
}: {
  visible: boolean;
  mensaje?: string;
}) {
  // Se mantiene montada durante el desvanecido para que no corte en seco.
  const [montada, setMontada] = useState(visible);

  useEffect(() => {
    if (visible) {
      setMontada(true);
      return;
    }
    const t = setTimeout(() => setMontada(false), 520);
    return () => clearTimeout(t);
  }, [visible]);

  if (!montada) return null;

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex flex-col items-center justify-center bg-petroleo-900',
        'transition-opacity duration-500 motion-reduce:transition-none',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      role="status"
      aria-live="polite"
      aria-label={mensaje}
    >
      {/* Halo tenue detrás del logo */}
      <div
        aria-hidden
        className="pointer-events-none absolute h-[26rem] w-[26rem] rounded-full opacity-60 blur-3xl"
        style={{
          background:
            'radial-gradient(circle, rgba(27,98,115,.55) 0%, rgba(10,42,51,0) 68%)',
        }}
      />

      <div className="relative flex flex-col items-center">
        <div className="animar-entrada relative overflow-hidden rounded-[22px] shadow-tarjeta ring-1 ring-white/10">
          <img
            src="/icons/icon-512.png"
            alt="Xtreme Mining Ltda."
            width={132}
            height={132}
            className="block h-[132px] w-[132px]"
          />
          {/* Barrido de luz sobre el logo */}
          <span aria-hidden className="barrido pointer-events-none absolute inset-0" />
        </div>

        <p className="animar-texto mt-6 text-sm font-semibold tracking-wide text-texto">
          XTREME MINING
        </p>
        <p className="animar-texto mt-1 text-[11px] tracking-wider text-texto-mute">
          CONTROL DE HORMIGÓN
        </p>

        {/* Barra de avance indeterminada */}
        <div className="mt-7 h-[3px] w-40 overflow-hidden rounded-full bg-white/8">
          <span aria-hidden className="avance block h-full w-1/3 rounded-full bg-fluor-verde" />
        </div>

        <p className="mt-3 text-[11px] text-texto-mute">{mensaje}</p>
      </div>
    </div>
  );
}
